import React from "react";
import { Link } from "react-router-dom";
import {
  Link as LinkIcon,
  Instagram,
  Facebook,
  Youtube,
  MessageCircle,
  Music2,
  Mail,
  Globe,
  Ticket,
  Send,
  Calendar,
  Sparkles,
  ShoppingBag,
  Phone,
  MapPin,
  Users,
  ExternalLink,
} from "lucide-react";

const LINK_ICONS = {
  link: LinkIcon,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  whatsapp: MessageCircle,
  tiktok: Music2,
  email: Mail,
  website: Globe,
  ticket: Ticket,
  send: Send,
  calendar: Calendar,
  sparkles: Sparkles,
  shop: ShoppingBag,
  phone: Phone,
  location: MapPin,
  users: Users,
};

const hexToRgba = (hex, a = 1) => {
  if (!hex) return `rgba(245,158,11,${a})`;
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${a})`;
};

const socialIconFor = (key) => {
  const map = {
    social_facebook: Facebook,
    social_instagram: Instagram,
    social_youtube: Youtube,
    social_tiktok: Music2,
    social_website: Globe,
    social_custom: LinkIcon,
  };
  return map[key] || LinkIcon;
};

export const InactiveLandingView = ({ show }) => {
  const ac = show?.accent_color || "#f59e0b";
  const fit = show?.inactive_landing_image_fit || "cover";
  const imageUrl = show?.inactive_landing_image || "";

  const links = (show?.inactive_landing_links || []).filter((l) => !l.hidden);

  // Overrides específicos da página de entrada (fallback ao show principal)
  const eventName = show?.inactive_landing_name || show?.name || "";
  const footerText = show?.inactive_landing_footer_text || show?.footer_text || "";
  const footerCopyright = show?.inactive_landing_footer_copyright || show?.footer_copyright || "";

  const socialEntries = [
    ["social_facebook", show?.social_facebook, show?.show_social_facebook],
    ["social_instagram", show?.social_instagram, show?.show_social_instagram],
    ["social_youtube", show?.social_youtube, show?.show_social_youtube],
    ["social_tiktok", show?.social_tiktok, show?.show_social_tiktok],
    ["social_website", show?.social_website, show?.show_social_website],
    ["social_custom", show?.social_custom, show?.show_social_custom],
  ].filter(([_, url, on]) => url && on !== false);

  const hasFullBg = show?.inactive_landing_show_image !== false && imageUrl && fit === "cover";

  return (
    <div
      className="min-h-screen relative overflow-x-hidden text-white"
      data-testid="inactive-landing"
      style={{
        background: "#09090b",
        "--ac": ac,
      }}
    >
      {/* Full background image (cover) */}
      {hasFullBg && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.9) 100%)",
            }}
          />
        </>
      )}

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center px-5 sm:px-6 pt-10 sm:pt-16 pb-10">
          <div className="w-full max-w-md flex flex-col items-center text-center">
            {/* Event name (small badge) */}
            {show?.inactive_landing_show_name !== false && eventName && (
              <div
                className="text-[11px] sm:text-xs font-semibold tracking-[0.28em] uppercase mb-4 px-3 py-1 rounded-full border"
                style={{
                  color: ac,
                  borderColor: hexToRgba(ac, 0.5),
                  backgroundColor: hexToRgba(ac, 0.08),
                }}
              >
                {eventName}
              </div>
            )}

            {/* Header image (contain / full-width above content) */}
            {show?.inactive_landing_show_image !== false && imageUrl && fit !== "cover" && (
              <div
                className={`w-full mb-8 rounded-2xl overflow-hidden ${
                  fit === "contain" ? "bg-black/40 border border-white/10" : ""
                }`}
                style={{
                  boxShadow: `0 20px 60px -20px ${hexToRgba(ac, 0.35)}`,
                }}
              >
                <img
                  src={imageUrl}
                  alt={show?.name || "Imagem"}
                  className={fit === "full" ? "w-full h-40 object-cover" : "w-full max-h-[60vh] object-contain"}
                />
              </div>
            )}

            {/* Small text */}
            {show?.inactive_landing_show_small_text !== false && show?.inactive_landing_small_text && (
              <p className="text-[13px] tracking-widest uppercase text-zinc-400 mb-3">
                {show.inactive_landing_small_text}
              </p>
            )}

            {/* Main title */}
            {show?.inactive_landing_show_title !== false && show?.inactive_landing_title && (
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-4"
                style={{ color: "#fff", textShadow: hasFullBg ? "0 2px 12px rgba(0,0,0,0.6)" : "none" }}
              >
                {show.inactive_landing_title}
              </h1>
            )}

            {/* Message */}
            {show?.inactive_landing_show_message !== false && show?.inactive_landing_message && (
              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed max-w-md mb-4">
                {show.inactive_landing_message}
              </p>
            )}

            {/* Dates */}
            {show?.inactive_landing_show_dates !== false && show?.inactive_landing_dates && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-8"
                style={{
                  color: ac,
                  backgroundColor: hexToRgba(ac, 0.12),
                  border: `1px solid ${hexToRgba(ac, 0.35)}`,
                }}
              >
                <Calendar className="w-4 h-4" />
                {show.inactive_landing_dates}
              </div>
            )}

            {/* Links list */}
            {show?.inactive_landing_show_links !== false && links.length > 0 && (
              <div className="w-full space-y-3 mt-4" data-testid="inactive-landing-links">
                {links.map((l) => {
                  const Icon = LINK_ICONS[l.icon] || LinkIcon;
                  return (
                    <a
                      key={l.id}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`inactive-link-${l.id}`}
                      className="group flex items-center gap-4 w-full px-4 py-3.5 rounded-2xl border transition-all"
                      style={{
                        backgroundColor: hasFullBg ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                        borderColor: hasFullBg ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)",
                        backdropFilter: hasFullBg ? "blur(12px)" : "none",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = ac;
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = hasFullBg
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(255,255,255,0.08)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: hexToRgba(ac, 0.18), color: ac }}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="font-semibold text-white text-sm sm:text-[15px] leading-tight uppercase tracking-wide truncate">
                          {l.title}
                        </div>
                        {l.subtitle && (
                          <div className="text-[12px] text-zinc-400 mt-0.5 truncate">{l.subtitle}</div>
                        )}
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-white shrink-0 transition-colors" />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Social icons */}
            {show?.inactive_landing_show_social === true && socialEntries.length > 0 && show?.show_social_links !== false && (
              <div className="flex items-center justify-center gap-3 mt-8">
                {socialEntries.map(([key, url]) => {
                  const Icon = socialIconFor(key);
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:border-white/40 hover:bg-white/5 transition-all"
                      aria-label={key}
                    >
                      <Icon className="w-4 h-4 text-zinc-300" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {show?.inactive_landing_show_footer !== false && (
          <footer className="w-full px-6 pb-6 pt-4 flex flex-col items-center gap-1.5 text-[11px] text-zinc-500">
            {show?.show_footer_text !== false && footerText && (
              <div>© {new Date().getFullYear()} {eventName} · {footerText}</div>
            )}
            {show?.show_footer_copyright !== false && footerCopyright && (
              <div>{footerCopyright}</div>
            )}
            {show?.inactive_landing_admin_button_visible !== false && (
              <Link
                to="/admin/login"
                data-testid="inactive-landing-admin-link"
                className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-600 hover:text-zinc-300 transition-colors"
              >
                {show?.inactive_landing_admin_button_text || "Admin"}
              </Link>
            )}
          </footer>
        )}
      </div>
    </div>
  );
};

export default InactiveLandingView;
